import type { Landmark } from '../../../data/types';
import type { Ring } from '../../../geo/types';
import { vesselOutline } from './churchVessel';
import { conventEnclosureRings } from './conventEnclosure';
import { conventRangeRect } from './conventRange';
import { corners } from './genericBlock';
import reading from './heiligewegClarissenPlan.json';
import { planFrameOf, toLayout, type PlanFrame } from './planFrame';

export function heiligewegClarissenReading(landmark: Landmark) {
  if (landmark.id !== 'landmark-clarissen')
    throw new Error(`No Heiligeweg Clarissen plan for ${landmark.id}`);
  return reading;
}

/** Retain the existing fitted precinct anchor and heading. */
export const heiligewegClarissenFrame = (landmark: Landmark): PlanFrame =>
  planFrameOf(landmark.footprint, landmark.heading);

export function heiligewegClarissenPartFrame(
  plan: PlanFrame,
  part: { readonly u: number; readonly v: number; readonly angleDeg: number }
): PlanFrame {
  return {
    origin: toLayout(plan, [part.u, part.v]),
    heading: plan.heading + (part.angleDeg * Math.PI) / 180,
    ring: [],
  };
}

export const heiligewegClarissenChapel = (landmark: Landmark) => ({
  ...heiligewegClarissenReading(landmark).chapel,
  apse: 'flat' as const,
});

export function heiligewegClarissenChapelFrame(landmark: Landmark): PlanFrame {
  return heiligewegClarissenPartFrame(
    heiligewegClarissenFrame(landmark),
    heiligewegClarissenReading(landmark).chapel
  );
}

/** Grounded rooms only; the private gate and open ground receive no blocking slab. */
export function heiligewegClarissenFoundations(landmark: Landmark): Ring[] {
  const plan = heiligewegClarissenFrame(landmark);
  return [
    vesselOutline(heiligewegClarissenChapel(landmark)).map((p) =>
      toLayout(heiligewegClarissenChapelFrame(landmark), p)
    ),
    ...heiligewegClarissenReading(landmark).ranges.flatMap((part) => {
      const rect = conventRangeRect(part),
        frame = heiligewegClarissenPartFrame(plan, part),
        centre = 'passageU' in part ? part.passageU : 0;
      return (
        part.passage
          ? [
              { ...rect, u1: centre - part.passage / 2 },
              { ...rect, u0: centre + part.passage / 2 },
            ]
          : [rect]
      ).map((r) => corners(r).map((p) => toLayout(frame, p)));
    }),
  ];
}

export function heiligewegClarissenObstacles(landmark: Landmark): Ring[] {
  const plan = heiligewegClarissenFrame(landmark);
  return [
    ...heiligewegClarissenFoundations(landmark),
    ...conventEnclosureRings(heiligewegClarissenReading(landmark).walls).map((r) =>
      r.map((p) => toLayout(plan, p))
    ),
  ];
}

export function heiligewegClarissenFloors(landmark: Landmark) {
  const plan = heiligewegClarissenFrame(landmark),
    reading = heiligewegClarissenReading(landmark);
  return [
    ...reading.floors.map((f) => ({ ...f, ring: f.ring.map((p) => [p[0]!, p[1]!] as const) })),
    ...reading.ranges
      .filter((p) => p.passage)
      .map((p) => {
        const centre = 'passageU' in p ? p.passageU : 0;
        return {
          ring: corners({
            u0: centre - p.passage / 2,
            u1: centre + p.passage / 2,
            v0: -p.width / 2,
            v1: p.width / 2,
          }).map((q) =>
            toLayout({ origin: [p.u, p.v], heading: (p.angleDeg * Math.PI) / 180, ring: [] }, q)
          ),
          height: 0.025,
          garden: false,
        };
      }),
  ].map((f) => ({ ...f, ring: f.ring.map((p) => toLayout(plan, p)) }));
}
